import BaseController from "../../../../controller/BaseController";
import JSONModel from "sap/ui/model/json/JSONModel";
import ODataModelUtils from "./ODataModelUtils";
export default class PersonalizationUtils {

    /**
     * Loads the personalization data for the current user and sets it to the view's "personalization" model.
     * This method retrieves the user's configuration from the OData model, parses it, and sets it to a JSONModel.
     * If no personalization data is found, it initializes an empty JSONModel.
     * @param controller The BaseController instance managing the view.
     * @throws Error if there is an issue retrieving or parsing the personalization data.
     */
	public static async loadPersonalization(controller: BaseController): Promise<void> {
		const user = controller.getOwnerComponent().getUser();
		const propertyBinding = controller
        .getOwnerComponent()
			.getODataModel()
			.bindProperty(`/authuser(${user.getId()})/user_config`);
		try {
			const personalizationData =
				(await propertyBinding.requestValue()) as string;
			if (!personalizationData) {
				console.warn("No personalization data found for user.");
				controller.getOwnerComponent().setModel(new JSONModel({}), "personalization");
				return;
			}
			const personalizationModel = new JSONModel();
			const parsedData = JSON.parse(personalizationData) as Record<
				string,
				unknown
			>;
			if (typeof parsedData === "object" && parsedData !== null) {
				personalizationModel.setData(parsedData);
			} else {
				console.warn("Parsed personalization data is not an object.");
				personalizationModel.setData({});
			}
			controller.getOwnerComponent().setModel(personalizationModel, "personalization");
		} catch (error) {
			console.error("Error loading personalization data:", error);
			throw new Error("Failed to load personalization data.");
		}
	}

    /**
     * Saves the personalization data for the current user.
     * This method retrieves the personalization model from the view,
     * serializes its data, and updates the user's configuration in the OData model.
     * @param controller The BaseController instance managing the view.
     * @throws Error if the personalization model is not found or if the data is not an object.
     */
    public static async savePersonalization(controller: BaseController): Promise<void> {
        // Retrieve the user and personalization model
        const user = controller.getOwnerComponent().getUser();
        const personalizationModel = controller.getOwnerComponent().getModel("personalization") as JSONModel;
        if (!personalizationModel) {
            console.error("Personalization model not found.");
            throw new Error("Personalization model not found.");
        }
        // Ensure the personalization data is an object before saving
        const personalizationData = personalizationModel.getData() as Record<string, unknown>;
        if (typeof personalizationData !== "object" || personalizationData === null) {
            console.error("Personalization data is not an object.");
            throw new Error("Personalization data is not an object.");
        }
        try {
            // read Value before setting it
            await ODataModelUtils.setValue(controller.getOwnerComponent().getODataModel(), `/authuser(${user.getId()})`, "user_config", JSON.stringify(personalizationData));
        }
        catch (error) {
            console.error("Error saving personalization data:", error);
            throw new Error("Failed to save personalization data.");
        }
    }

    public static async savePersonalizationForSubtask(
        controller: BaseController,
        subtaskName: string,
        personalizationData: Record<string, unknown>
    ): Promise<void> {
        const personalizationModel = controller.getOwnerComponent().getModel("personalization") as JSONModel;
        if (!personalizationModel) {
            console.error("Personalization model not found.");
            throw new Error("Personalization model not found.");
        }
        const currentData = personalizationModel.getData() as Record<string, unknown>;
        if (!currentData || typeof currentData !== "object") {
            console.error("Current personalization data is not an object.");
            throw new Error("Current personalization data is not an object.");
        }
        // Update the specific subtask's personalization data
        currentData[subtaskName] = personalizationData;
        personalizationModel.setData(currentData);
        await this.savePersonalization(controller);
    }
}
